<template lang="pug">
section
  template(v-if="showPermissionError")
    h2.heading
      | This resource is only visible to users with permissions:
      br
      | CLINICAL_PORTAL or COACH_MANAGEMENT
  template(v-else-if="!hasMemberId")
    h2.heading
      | A member identifier must be provided
      br
      | Did you come here through Clinical Portal?  For admin access, add a `uuid` query param.
  template(v-else="")
    h2.heading Coaching sessions
      div.schedule-alert-wrapper(v-if="showScheduleError")
        .icon
          svg(width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg")
            path(d="M8 -0.25C3.58065 -0.25 0 3.3629 0 7.75C0 12.1694 3.58065 15.75 8 15.75C12.3871 15.75 16 12.1694 16 7.75C16 3.3629 12.3871 -0.25 8 -0.25ZM8 14.2016C4.41935 14.2016 1.54839 11.3306 1.54839 7.75C1.54839 4.20161 4.41935 1.29839 8 1.29839C11.5484 1.29839 14.4516 4.20161 14.4516 7.75C14.4516 11.3306 11.5484 14.2016 8 14.2016ZM9.35484 10.8468C9.35484 10.1048 8.74194 9.49194 8 9.49194C7.22581 9.49194 6.64516 10.1048 6.64516 10.8468C6.64516 11.621 7.22581 12.2016 8 12.2016C8.74194 12.2016 9.35484 11.621 9.35484 10.8468ZM6.70968 4.04032L6.93548 8.42742C6.93548 8.62097 7.12903 8.78226 7.32258 8.78226H8.64516C8.83871 8.78226 9.03226 8.62097 9.03226 8.42742L9.25806 4.04032C9.25806 3.81452 9.09677 3.62097 8.87097 3.62097H7.09677C6.87097 3.62097 6.70968 3.81452 6.70968 4.04032Z" fill="#CD000A")
        | Please update sessions that occurred in the past.
  section.loading-dots(v-if="!sessionTableDataLoaded && !showPermissionError && hasMemberId")
    .dot1
    .dot2
    .dot3

  section(v-else-if="sessionTableDataLoaded")
    template(v-if="clinicalCoachingSessionTableData.length === 0")
      p This member hasn't completed any coaching sessions.
    template(v-else="")
      div.sessions-table-wrapper
        VueTable(
          :columns="columnLabels"
          :rows="clinicalCoachingSessionTableData")
          template(slot-scope="{ row }" slot="status")
            .session-status
              .icon(v-if="row.status === 'Scheduled' && isInPast(row.startTime)" title="This session occurred in the past. Please update to a valid status.")
                svg(width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg")
                  path(d="M8 -0.25C3.58065 -0.25 0 3.3629 0 7.75C0 12.1694 3.58065 15.75 8 15.75C12.3871 15.75 16 12.1694 16 7.75C16 3.3629 12.3871 -0.25 8 -0.25ZM8 14.2016C4.41935 14.2016 1.54839 11.3306 1.54839 7.75C1.54839 4.20161 4.41935 1.29839 8 1.29839C11.5484 1.29839 14.4516 4.20161 14.4516 7.75C14.4516 11.3306 11.5484 14.2016 8 14.2016ZM9.35484 10.8468C9.35484 10.1048 8.74194 9.49194 8 9.49194C7.22581 9.49194 6.64516 10.1048 6.64516 10.8468C6.64516 11.621 7.22581 12.2016 8 12.2016C8.74194 12.2016 9.35484 11.621 9.35484 10.8468ZM6.70968 4.04032L6.93548 8.42742C6.93548 8.62097 7.12903 8.78226 7.32258 8.78226H8.64516C8.83871 8.78226 9.03226 8.62097 9.03226 8.42742L9.25806 4.04032C9.25806 3.81452 9.09677 3.62097 8.87097 3.62097H7.09677C6.87097 3.62097 6.70968 3.81452 6.70968 4.04032Z" fill="#CD000A")
              .icon(v-else="")
                svg(width="16" height="16" viewBox="0 0 16 16" fill="none")
              select(:class="{ 'form-control-error': row.status === 'Scheduled' && isInPast(row.startTime) }" name="status" @change="changeSessionStatus($event, row.id)")
                option(:selected="row.status === 'Scheduled'" value="SCHEDULED") Scheduled
                option(:selected="row.status === 'Completed'" value="COMPLETED") Completed
                option(:selected="row.status === 'Cancelled'" value="CANCELLED") Cancelled
                option(:selected="row.status === 'No_show'" value="NO_SHOW") No Show
          template(slot-scope="{ row }" slot="recording")
            template(v-if="row.recording.recordingStatus === 'AVAILABLE' && row.recording.recordingId && showConferenceRecording")
              a.link-download-recording(@click="startDownload(row.recording.recordingId)")
                | Download recording
                svg.icon(width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg")
                  path(d="M12.5786 7.17857C12.7571 7 12.7571 6.71429 12.5786 6.57143L11.8643 5.85714C11.7214 5.67857 11.4357 5.67857 11.2571 5.85714L7.89999 9.32143V0.428571C7.89999 0.214286 7.72142 0 7.47142 0H6.47142C6.25714 0 6.04285 0.214286 6.04285 0.428571V9.32143L2.72142 5.85714C2.54285 5.67857 2.25714 5.67857 2.11428 5.85714L1.39999 6.57143C1.22142 6.71429 1.22142 7 1.39999 7.17857L6.68571 12.4643C6.86428 12.6429 7.11428 12.6429 7.29285 12.4643L12.5786 7.17857ZM13.4357 14.1429H0.578565C0.328565 14.1429 0.149994 14.3571 0.149994 14.5714V15.5714C0.149994 15.8214 0.328565 16 0.578565 16H13.4357C13.65 16 13.8643 15.8214 13.8643 15.5714V14.5714C13.8643 14.3571 13.65 14.1429 13.4357 14.1429Z" fill="#0078BC")
              a.hidden-download(
                v-if="showDownloadLinks"
                hidden
                aria-hidden="true"
                download
                target="_blank"
                :ref="`downloadLink_${row.recording.recordingId}`"
                :href="`${downloadUrls[row.recording.recordingId]}`")
            template(v-else-if="row.recording.recordingStatus === 'NOT_ACCESSIBLE' && showConferenceRecording")
              | You do not have access to this recording
            template(v-else-if="row.recording.recordingStatus === 'NOT_AVAILABLE' && !(row.status === 'Scheduled' || row.status === 'In_progress') && showConferenceRecording")
              | Session not recorded
            template(v-else="")
              //- NOOP: empty cell
              span
</template>
<script>
import { mapGetters, mapActions } from 'vuex';

// Provides sortable table
import VueTable from '@lossendae/vue-table';
import auth from '@/services/authSvc';
import moment from 'moment';

export default {
  name: 'PatientClinicalCoaching',
  components: { VueTable },
  props: {
    patientId: {
      type: Number,
      required: true
    },
    showPermissionError: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showDownloadLinks: true,
      downloadUrls: {},
      isClinicalPortal: false,
      columnLabels: [
        {
          name: 'date',
          title: 'Date'
        },
        {
          name: 'time',
          title: 'Time'
        },
        {
          name: 'coachName',
          title: 'Coach'
        },
        {
          name: 'status',
          title: 'Session Status'
        },
        {
          name: 'recording',
          title: 'Recordings'
        }
      ]
    };
  },
  computed: {
    hasMemberId() {
      return auth.getTokenLivongoUuid() || this.hasQueryUuid();
    },
    hasQueryUuid() {
      return !!this.$route.query.uuid;
    },
    showConferenceRecording() {
      const conferenceRecordingFeatureFlag =
        this.apiConfig.platforms &&
        this.apiConfig.platforms.web.features.conference_recording;
      return this.isClinicalPortal && conferenceRecordingFeatureFlag;
    },
    showScheduleError() {
      let hasError = false;
      const currentTime = moment();
      let appointmentTime = moment();
      if (
        !this.clinicalCoachingSessionTableData ||
        this.clinicalCoachingSessionTableData.length === 0
      ) {
        return false;
      }
      this.clinicalCoachingSessionTableData.forEach((item, index) => {
        appointmentTime = moment(item.startTime);
        console.log('item.startTime: ', appointmentTime);
        if (
          item.status === 'Scheduled' &&
          currentTime.isAfter(appointmentTime)
        ) {
          hasError = true;
        }
      });

      return hasError;
    },
    ...mapGetters([
      'sessionTableDataLoaded',
      'clinicalCoachingSessionTableData',
      'apiConfig'
    ])
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      const patientLivongoUuid =
        auth.getTokenLivongoUuid() || this.$route.query.uuid;
      if (!patientLivongoUuid) {
        return;
      }
      this.getMemberClinicalCoachingSessions({
        patientLivongoUuid,
        hasQueryUuid: this.hasQueryUuid
      });
      this.getMemberClinicalCoachingConferences({
        patientLivongoUuid,
        hasQueryUuid: this.hasQueryUuid
      });
    },
    isInPast(startTime) {
      const currentTime = moment();
      const appointmentTime = moment(startTime);
      return currentTime.isAfter(appointmentTime);
    },
    changeSessionStatus($event, sessionId) {
      const sessionStatus = $event.target.value;
      this.updateSessionStatus({
        sessionId,
        status: sessionStatus,
        hasQueryUuid: this.hasQueryUuid
      }).then(
        resp => {
          this.init();
          $event.target.blur();
        },
        () => {
          $event.target.blur();
        }
      );
    },
    startDownload(recordingId) {
      this.getConferenceRecordingDownloadURL(recordingId).then(resp => {
        this.downloadUrls[recordingId] = resp.data.downloadUrl;
        this.updateDownloadLinks();

        // Click the link
        setTimeout(() => {
          this.$refs[`downloadLink_${recordingId}`].click();
        }, 100);
      });
    },
    updateDownloadLinks() {
      // This method used due to vue2 reactivity issues with data object values
      // Causes reflow on hidden download link tags
      this.showDownloadLinks = false;
      this.showDownloadLinks = true;
    },
    ...mapActions([
      'getConferenceRecordingDownloadURL',
      'getMemberClinicalCoachingSessions',
      'getMemberClinicalCoachingConferences',
      'updateSessionStatus'
    ])
  }
};
</script>
<style lang="scss">
// NOTE !UNSCOPED!
// - Only use for overrides to VueTable styling
.sessions-table-wrapper {
  .d-flex {
    display: -webkit-box !important;
    display: -ms-flexbox !important;
    display: flex !important;
  }
  table {
    table-layout: auto;
    .session-status {
      display: flex;
      max-width: 200px;
    }
    thead tr {
      th {
        padding-top: 6px;
        padding-bottom: 8px;
        border-bottom: 1px solid #bebebe;
        text-transform: uppercase;
        a {
          white-space: normal;
        }
      }
    }

    tr {
      border-bottom: 1px solid #bebebe;
      box-sizing: border-box;
      &:last-child {
        border-bottom: 0px solid #fff;
      }

      th,
      td {
        padding: $small-spacing $small-spacing;
        &:first-child {
          padding-left: 0;
        }
      }
    }
    td {
      border-bottom: 0px;
    }
    .icon {
      display: flex;
      padding: 12px $small-spacing 0px;
    }
    .form-control-error {
      background-color: lighten($error-color, 45%);
      border-block-color: $error-color;
      scrollbar-arrow-color: $error-color;
      max-width: 200px;
    }
    .button-wrapper {
      text-align: right;
      button {
        display: inline;
        margin: 0 6px 0 0;
        padding: 8px 16px;

        &:last-child {
          margin-right: 0;
        }
      }
    }
    .tags {
      padding: 0px 5px 3px;
      .tag {
        display: inline-block;
        background-color: $action-color;
        padding: 3px 6px;
        border-radius: 3px;
        margin: 6px 6px 0 0;
        color: #fff;
        line-height: 1.2;
      }
    }
    .align-right {
      text-align: right;
    }
  }
}
</style>
<style lang="scss" scoped>
// Scoped styles
.heading .icon {
  margin-right: 8px;
  display: inline-block;
}
.schedule-alert-wrapper {
  color: $error-color;
}
.link-download-recording {
  cursor: pointer;

  &:hover .icon {
    path {
      fill: $hover-color;
    }
  }
  .icon {
    display: inline-block;
    margin: 0 0 0 6px;
    position: flex;
    top: 3px;
  }
}
</style>
